import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { planAPI, branchAPI } from '../../services/api';
import BandwidthManager from '../BandwidthManager/BandwidthManager';
import { useBranchStore, useAuthStore } from '../../store';
import { toast } from 'react-toastify';
import usePermission from '../../hooks/usePermission';
import { speedRangeText, dataLimitText, validityText, toKbps, toMbps, MAX_MBPS } from '../../utils/bandwidthFormat';
import './Plans.css';

const VALIDITY_UNITS = [
    { value: 'minutes', label: 'Minutes' },
    { value: 'hours', label: 'Hours' },
    { value: 'days', label: 'Days' },
    { value: 'months', label: 'Months' },
];

// The plan form, empty. Speeds in Mbps (as entered), data in the unit chosen.
const blankForm = (branchId = '') => ({
    name: '',
    type: 'basic',
    audience: 'guest',
    price: '',
    validity: '',
    validityUnit: 'days',
    dataUnlimited: false,
    dataLimit: '',
    dataUnit: 'MB',
    uploadSpeed: '',      // Mbps — the most
    downloadSpeed: '',    // Mbps
    minUploadSpeed: '',   // Mbps — guaranteed, empty = none
    minDownloadSpeed: '', // Mbps
    queueType: 'per_user',
    maxSessionDuration: '', // minutes, empty = unlimited
    idleTimeout: '',         // minutes, empty = unlimited
    maxConcurrentSessions: 1,
    branchId,
    description: ''
});

/** What is wrong with the speeds entered, or null: each up to 50 Mbps, a minimum no higher than its maximum. */
export function speedFormError(form) {
    const checks = [
        ['Download speed', form.downloadSpeed], ['Upload speed', form.uploadSpeed],
        ['Minimum download speed', form.minDownloadSpeed], ['Minimum upload speed', form.minUploadSpeed],
    ];
    for (const [label, value] of checks) {
        if (value === '' || value === null || value === undefined) continue;
        if (Number(value) < 0) return `${label} cannot be negative`;
        if (Number(value) > MAX_MBPS) return `${label} can be at most ${MAX_MBPS} Mbps`;
    }
    if (form.minDownloadSpeed !== '' && Number(form.downloadSpeed) > 0 && Number(form.minDownloadSpeed) > Number(form.downloadSpeed)) {
        return 'Minimum download speed cannot be higher than the download speed';
    }
    if (form.minUploadSpeed !== '' && Number(form.uploadSpeed) > 0 && Number(form.minUploadSpeed) > Number(form.uploadSpeed)) {
        return 'Minimum upload speed cannot be higher than the upload speed';
    }
    return null;
}

const Plans = () => {
    const { selectedBranch } = useBranchStore();
    const { canCreate, canEdit, canDelete } = usePermission('plans');
    const canManage = canCreate || canEdit || canDelete;
    // Two views: the plans, and the guests online on them live (Sessions → View).
    const { canView: canSeeLive } = usePermission('sessions');
    const [searchParams, setSearchParams] = useSearchParams();
    const view = canSeeLive && searchParams.get('tab') === 'bandwidth' ? 'bandwidth' : 'plans';
    const openView = (next) => setSearchParams(next === 'bandwidth' ? { tab: 'bandwidth' } : {});
    const [plans, setPlans] = useState([]);
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingPlan, setEditingPlan] = useState(null);
    const [viewingPlan, setViewingPlan] = useState(null);
    const [activeTab, setActiveTab] = useState('all');
    const [branchFilter, setBranchFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState(''); // Added search state

    // Delete Modal State
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [planToDelete, setPlanToDelete] = useState(null);

    const [formData, setFormData] = useState(blankForm());
    const speedError = speedFormError(formData);

    const audienceOptions = [
        { value: 'guest', label: 'Guest' },
        { value: 'staff', label: 'Staff' },
        { value: 'resident', label: 'Resident' },
        { value: 'visitor', label: 'Visitor' },
        { value: 'vip', label: 'VIP' },
        { value: 'corporate', label: 'Corporate' },
        { value: 'long_stay', label: 'Long-Stay Guest' },
        { value: 'otp', label: 'OTP Login' },
        { value: 'any', label: 'Any / Unassigned' },
    ];

    useEffect(() => {
        fetchData();
    }, [selectedBranch]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const branchId = selectedBranch ? selectedBranch._id : undefined;

            const [plansRes, branchesRes] = await Promise.all([
                planAPI.getAll(branchId),
                branchAPI.getAll()
            ]);

            setPlans(plansRes.data.data.plans || []);
            setBranches(branchesRes.data.data.branches || []);
        } catch (error) {
            toast.error('Failed to fetch data');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Find selected branch to get tenantId
        const selectedBranchObj = branches.find(b => b._id === formData.branchId);
        if (!selectedBranchObj) {
            toast.error('Please select a valid branch');
            return;
        }
        if (speedError) {
            toast.error(speedError);
            return;
        }

        // A plan's other settings (burst, peak hours, daily / monthly data) the form does
        // not show are kept: a sub-object sent replaces the stored one.
        const kept = editingPlan || {};
        const unlimited = formData.dataUnlimited || formData.type === 'unlimited' || !Number(formData.dataLimit);

        // Prepare payload correctly mapped to backend schema
        const payload = {
            tenantId: selectedBranchObj.tenantId,
            branchId: formData.branchId,
            name: formData.name,
            type: formData.type,
            audience: formData.audience,
            pricing: {
                amount: Number(formData.price) || 0,
                currency: 'INR',
                isFree: Number(formData.price) === 0
            },
            validity: {
                duration: Number(formData.validity),
                unit: formData.validityUnit,
                autoRenew: kept.validity?.autoRenew || false
            },
            // Mbps → kbps; the minimum (guaranteed) speed only when one is entered
            bandwidth: {
                ...(kept.bandwidth || {}),
                uploadSpeed: toKbps(formData.uploadSpeed || 0),
                downloadSpeed: toKbps(formData.downloadSpeed || 0),
                guaranteedUpload: formData.minUploadSpeed === '' ? 0 : toKbps(formData.minUploadSpeed),
                guaranteedDownload: formData.minDownloadSpeed === '' ? 0 : toKbps(formData.minDownloadSpeed),
            },
            queueType: formData.queueType,
            // MB on the plan, whichever unit it was entered in (0 = unlimited)
            dataLimit: {
                ...(kept.dataLimit || {}),
                total: unlimited ? 0 : Math.round(Number(formData.dataLimit) * (formData.dataUnit === 'GB' ? 1024 : 1)),
                unit: formData.dataUnit,
            },
            sessionLimits: {
                maxDuration: formData.maxSessionDuration ? Number(formData.maxSessionDuration) : 0, // minutes, 0 = unlimited
                idleTimeout: formData.idleTimeout ? Number(formData.idleTimeout) : 0, // minutes, 0 = unlimited
                maxConcurrentSessions: Number(formData.maxConcurrentSessions) || 1
            },
            description: formData.description
        };

        try {
            if (editingPlan) {
                await planAPI.update(editingPlan._id, payload);
                toast.success('Plan updated successfully');
            } else {
                await planAPI.create(payload);
                toast.success('Plan created successfully');
            }
            setShowModal(false);
            setEditingPlan(null);
            resetForm();
            fetchData();
        } catch (error) {
            console.error('Create Plan Error:', error.response?.data);
            toast.error(error.response?.data?.message || 'Operation failed');
        }
    };

    const handleEdit = (plan) => {
        setEditingPlan(plan);
        // Map backend structure back to form state
        const dataMB = Number(plan.dataLimit?.total) || 0;
        const dataUnit = plan.dataLimit?.unit === 'GB' ? 'GB' : 'MB';
        const bw = plan.bandwidth || {};
        setFormData({
            ...blankForm(),
            name: plan.name,
            type: plan.type,
            audience: plan.audience || 'guest',
            price: plan.pricing?.amount || '',
            validity: plan.validity?.duration || '',
            validityUnit: plan.validity?.unit || 'days',
            dataUnlimited: !dataMB,
            dataLimit: dataMB ? (dataUnit === 'GB' ? Math.round((dataMB / 1024) * 100) / 100 : dataMB) : '',
            dataUnit,
            // Convert Kbps to Mbps for display
            uploadSpeed: bw.uploadSpeed ? toMbps(bw.uploadSpeed) : '',
            downloadSpeed: bw.downloadSpeed ? toMbps(bw.downloadSpeed) : '',
            minUploadSpeed: bw.guaranteedUpload ? toMbps(bw.guaranteedUpload) : '',
            minDownloadSpeed: bw.guaranteedDownload ? toMbps(bw.guaranteedDownload) : '',
            queueType: plan.queueType || 'per_user',
            maxSessionDuration: plan.sessionLimits?.maxDuration || '',
            idleTimeout: plan.sessionLimits?.idleTimeout || '',
            maxConcurrentSessions: plan.sessionLimits?.maxConcurrentSessions || 1,
            branchId: plan.branchId?._id || plan.branchId || '',
            description: plan.description || ''
        });
        setShowModal(true);
    };

    const resetForm = () => {
        setEditingPlan(null);
        setFormData(blankForm(selectedBranch?._id || ''));
    };

    const confirmDelete = async () => {
        if (!planToDelete) return;
        try {
            await planAPI.delete(planToDelete._id);
            toast.success('Plan deleted');
            fetchData();
            setShowDeleteModal(false);
            setPlanToDelete(null);
        } catch (error) {
            console.error('Delete Plan Error:', error);
            toast.error(error.response?.data?.message || 'Failed to delete plan');
        }
    };

    const handleDelete = (plan) => {
        setPlanToDelete(plan);
        setShowDeleteModal(true);
    };

    const handleView = (plan) => {
        setViewingPlan(plan);
    };

    // A speed one way, with its guaranteed minimum when the plan has one
    const formatSpeed = (kbps, minKbps) => speedRangeText(kbps, minKbps);

    // Stats calculation
    const stats = {
        total: plans.length,
        basic: plans.filter(p => p.type === 'basic').length,
        premium: plans.filter(p => p.type === 'premium').length,
        unlimited: plans.filter(p => p.type === 'unlimited').length
    };

    const filteredPlans = plans.filter(plan => {
        const matchesTab = activeTab === 'all' || plan.type === activeTab;
        const matchesBranch = branchFilter === 'all' || (plan.branchId?._id || plan.branchId) === branchFilter;
        const matchesSearch = plan.name.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesTab && matchesBranch && matchesSearch;
    });

    // Group filtered plans by branch for the branch-wise sectioned view
    const plansByBranch = filteredPlans.reduce((groups, plan) => {
        const branchName = plan.branchId?.name || 'Unassigned';
        const key = plan.branchId?._id || plan.branchId || 'unassigned';
        if (!groups[key]) groups[key] = { branchName, plans: [] };
        groups[key].plans.push(plan);
        return groups;
    }, {});

    return (
        <div className="page-container-modern">
            {/* Header Section */}
            <div className="page-header-modern">
                <div className="header-content">
                    <div className="header-text">
                        <h1 className="page-title-modern">
                            <i className="fas fa-wifi"></i>
                            Internet Plans
                        </h1>
                        <p className="page-subtitle-modern">Data, time and speed plans for guests and vouchers — and each guest's speed, live</p>
                    </div>
                    {canCreate && view === 'plans' && (
                        <div className="header-actions">
                            <button className="btn-primary-modern" onClick={() => {
                                resetForm();
                                setShowModal(true);
                            }}>
                                <i className="fas fa-plus-circle"></i>
                                New Plan
                            </button>
                        </div>
                    )}
                </div>

                {canSeeLive && (
                    <div className="plans-views" role="tablist" aria-label="Internet Plans">
                        <button type="button" role="tab" aria-selected={view === 'plans'} className={view === 'plans' ? 'active' : ''} onClick={() => openView('plans')}>
                            <i className="fas fa-tags" aria-hidden="true"></i> Plans
                        </button>
                        <button type="button" role="tab" aria-selected={view === 'bandwidth'} className={view === 'bandwidth' ? 'active' : ''} onClick={() => openView('bandwidth')}>
                            <i className="fas fa-sliders-h" aria-hidden="true"></i> Live Bandwidth
                        </button>
                    </div>
                )}

                {/* Stats Cards */}
                {view === 'plans' && <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-icon blue">
                            <i className="fas fa-wifi"></i>
                        </div>
                        <div className="stat-details">
                            <span className="stat-value">{stats.total}</span>
                            <span className="stat-label">Total Plans</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon green">
                            <i className="fas fa-leaf"></i>
                        </div>
                        <div className="stat-details">
                            <span className="stat-value">{stats.basic}</span>
                            <span className="stat-label">Basic</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon purple">
                            <i className="fas fa-gem"></i>
                        </div>
                        <div className="stat-details">
                            <span className="stat-value">{stats.premium}</span>
                            <span className="stat-label">Premium</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon orange">
                            <i className="fas fa-infinity"></i>
                        </div>
                        <div className="stat-details">
                            <span className="stat-value">{stats.unlimited}</span>
                            <span className="stat-label">Unlimited</span>
                        </div>
                    </div>
                </div>}
            </div>

            {view === 'bandwidth' ? <BandwidthManager embedded /> : (<>
            {/* Filters */}
            <div className="filters-section">
                <div className="search-box-modern">
                    <i className="fas fa-search"></i>
                    <input
                        type="text"
                        placeholder="Search plans..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="filter-group">
                    <select
                        className="filter-select"
                        value={activeTab}
                        onChange={(e) => setActiveTab(e.target.value)}
                    >
                        <option value="all">All Types</option>
                        <option value="basic">Basic</option>
                        <option value="premium">Premium</option>
                        <option value="unlimited">Unlimited</option>
                        <option value="free">Free</option>
                    </select>
                </div>
                <div className="filter-group">
                    <select
                        className="filter-select"
                        value={branchFilter}
                        onChange={(e) => setBranchFilter(e.target.value)}
                    >
                        <option value="all">All Branches</option>
                        {branches.map(branch => (
                            <option key={branch._id} value={branch._id}>{branch.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {loading ? (
                <div className="loading-modern">
                    <div className="spinner-modern"></div>
                    <p>Loading plans...</p>
                </div>
            ) : filteredPlans.length === 0 ? (
                <div className="empty-state-modern">
                    <div className="empty-icon">
                        <i className="fas fa-box-open"></i>
                    </div>
                    <h3>No Plans Found</h3>
                    <p>Create a new plan to get started.</p>
                </div>
            ) : (
                <div className="plans-by-branch">
                    {Object.entries(plansByBranch).map(([branchKey, group]) => (
                        <div key={branchKey} className="branch-plan-section">
                            <h3 className="branch-section-title">
                                <i className="fas fa-building"></i>
                                {group.branchName}
                                <span className="branch-plan-count">{group.plans.length} plan{group.plans.length !== 1 ? 's' : ''}</span>
                            </h3>
                            <div className="table-wrapper-modern">
                                <div className="table-container">
                                    <table className="table-modern">
                                        <thead>
                                            <tr>
                                                <th>Plan Name</th>
                                                <th>Type</th>
                                                <th>Audience</th>
                                                <th>Price</th>
                                                <th>Validity</th>
                                                <th>Data Limit</th>
                                                <th>Download</th>
                                                <th>Upload</th>
                                                <th>Status</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {group.plans.map(plan => (
                                                <tr key={plan._id}>
                                                    <td>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                                                            <i className={`fas ${plan.type === 'unlimited' ? 'fa-infinity' : plan.type === 'premium' ? 'fa-gem' : 'fa-clock'}`} style={{ color: 'var(--primary-color)' }}></i>
                                                            {plan.name}
                                                            {plan.queueType === 'dynamic' && <span className="plan-dynamic-tag" title="Speed shared out live by the Dynamic Bandwidth Manager">Dynamic</span>}
                                                        </div>
                                                    </td>
                                                    <td className="capitalize">{plan.type}</td>
                                                    <td className="capitalize">{plan.audience || 'guest'}</td>
                                                    <td>₹{plan.pricing?.amount || 0}</td>
                                                    <td>{validityText(plan.validity)}</td>
                                                    <td>{dataLimitText(plan.dataLimit)}</td>
                                                    <td>{formatSpeed(plan.bandwidth?.downloadSpeed, plan.bandwidth?.guaranteedDownload)}</td>
                                                    <td>{formatSpeed(plan.bandwidth?.uploadSpeed, plan.bandwidth?.guaranteedUpload)}</td>
                                                    <td>
                                                        <span className={`status-badge ${plan.status === 'inactive' || plan.status === 'archived' ? 'inactive' : 'active'}`}>
                                                            <span className="badge-dot"></span>{plan.status || 'active'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="table-row-actions">
                                                            <button className="action-btn-icon view" onClick={() => handleView(plan)} title="View"><i className="fas fa-eye"></i></button>
                                                            {canEdit && <button className="action-btn-icon edit" onClick={() => handleEdit(plan)} title="Edit"><i className="fas fa-edit"></i></button>}
                                                            {canDelete && <button className="action-btn-icon delete" onClick={() => handleDelete(plan)} title="Delete"><i className="fas fa-trash"></i></button>}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            </>)}

            {viewingPlan && (
                <div className="modal-overlay-modern" onClick={() => setViewingPlan(null)}>
                    <div className="modal-modern" onClick={e => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <h2>{viewingPlan.name}</h2>
                            <button className="modal-close" onClick={() => setViewingPlan(null)}><i className="fas fa-times"></i></button>
                        </div>
                        <div className="modal-body-modern">
                            <div className="status-badges">
                                <span className={`status-badge ${viewingPlan.status === 'inactive' || viewingPlan.status === 'archived' ? 'inactive' : 'active'}`}>
                                    <span className="badge-dot"></span>{viewingPlan.status || 'active'}
                                </span>
                                <span className="status-badge inactive">₹{viewingPlan.pricing?.amount || 0}</span>
                            </div>
                            <div className="info-grid two-col">
                                <div className="info-item">
                                    <i className="fas fa-code-branch"></i>
                                    <div><span className="info-label">Branch</span><span className="info-value">{viewingPlan.branchId?.name || '—'}</span></div>
                                </div>
                                <div className="info-item">
                                    <i className={`fas ${viewingPlan.type === 'unlimited' ? 'fa-infinity' : viewingPlan.type === 'premium' ? 'fa-gem' : 'fa-clock'}`}></i>
                                    <div><span className="info-label">Type</span><span className="info-value capitalize">{viewingPlan.type} · {viewingPlan.audience || 'guest'}</span></div>
                                </div>
                                <div className="info-item">
                                    <i className="fas fa-calendar-check"></i>
                                    <div><span className="info-label">Validity</span><span className="info-value">{validityText(viewingPlan.validity)}</span></div>
                                </div>
                                <div className="info-item">
                                    <i className="fas fa-tachometer-alt"></i>
                                    <div><span className="info-label">Data Limit</span><span className="info-value">{dataLimitText(viewingPlan.dataLimit)}</span></div>
                                </div>
                                <div className="info-item">
                                    <i className="fas fa-download"></i>
                                    <div><span className="info-label">Download</span><span className="info-value">{formatSpeed(viewingPlan.bandwidth?.downloadSpeed, viewingPlan.bandwidth?.guaranteedDownload)}</span></div>
                                </div>
                                <div className="info-item">
                                    <i className="fas fa-upload"></i>
                                    <div><span className="info-label">Upload</span><span className="info-value">{formatSpeed(viewingPlan.bandwidth?.uploadSpeed, viewingPlan.bandwidth?.guaranteedUpload)}</span></div>
                                </div>
                                <div className="info-item">
                                    <i className="fas fa-sliders-h"></i>
                                    <div><span className="info-label">Speed control</span><span className="info-value">{viewingPlan.queueType === 'dynamic' ? 'Dynamic — shares the gateway\'s link' : 'Fixed per guest'}</span></div>
                                </div>
                                <div className="info-item">
                                    <i className="fas fa-hourglass-half"></i>
                                    <div><span className="info-label">Max Session</span><span className="info-value">{viewingPlan.sessionLimits?.maxDuration ? `${viewingPlan.sessionLimits.maxDuration} min` : 'Unlimited'}</span></div>
                                </div>
                                <div className="info-item">
                                    <i className="fas fa-users"></i>
                                    <div><span className="info-label">Max Devices</span><span className="info-value">{viewingPlan.sessionLimits?.maxConcurrentSessions || 1}</span></div>
                                </div>
                                {viewingPlan.description && (
                                    <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                                        <i className="fas fa-align-left"></i>
                                        <div><span className="info-label">Description</span><span className="info-value">{viewingPlan.description}</span></div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <button className="btn-secondary-modern" onClick={() => setViewingPlan(null)}>Close</button>
                            {canEdit && <button className="btn-primary-modern" onClick={() => { setViewingPlan(null); handleEdit(viewingPlan); }}><i className="fas fa-edit"></i> Edit</button>}
                        </div>
                    </div>
                </div>
            )}

            {showModal && (
                <div className="modal-overlay-modern" onClick={() => setShowModal(false)}>
                    <div className="modal-modern" onClick={e => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <div>
                                <h2>{editingPlan ? 'Edit Plan' : 'Create New Plan'}</h2>
                                <p className="page-subtitle-modern" style={{ fontSize: '0.9rem', margin: 0 }}>Configure internet access parameters</p>
                            </div>
                            <button className="modal-close" onClick={() => setShowModal(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body-modern">
                                <div className="form-section">
                                    <h3 className="section-title">
                                        <i className="fas fa-info-circle"></i>
                                        Plan Basics
                                    </h3>
                                    <div className="form-grid">
                                        <div className="form-group-modern">
                                            <label>Branch <span style={{ color: 'var(--danger-color)' }}>*</span></label>
                                            <select
                                                name="branchId"
                                                value={formData.branchId}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="">Select Branch</option>
                                                {branches.map(branch => (
                                                    <option key={branch._id} value={branch._id}>{branch.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="form-group-modern">
                                            <label>Plan Name <span style={{ color: 'var(--danger-color)' }}>*</span></label>
                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                placeholder="e.g. 1 Hour Free"
                                                required
                                            />
                                        </div>
                                        <div className="form-group-modern">
                                            <label>Type</label>
                                            <select
                                                name="type"
                                                value={formData.type}
                                                onChange={(e) => {
                                                    const newType = e.target.value;
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        type: newType,
                                                        dataLimit: newType === 'unlimited' ? '' : prev.dataLimit,
                                                        dataUnlimited: newType === 'unlimited' ? true : prev.dataUnlimited,
                                                        price: newType === 'free' ? '0' : prev.price
                                                    }));
                                                }}
                                            >
                                                <option value="basic">Basic (Data/Time Capped)</option>
                                                <option value="premium">Premium (High Speed)</option>
                                                <option value="unlimited">Unlimited Data</option>
                                                <option value="free">Free Plan</option>
                                            </select>
                                        </div>
                                        <div className="form-group-modern">
                                            <label>User Type</label>
                                            <select
                                                name="audience"
                                                value={formData.audience}
                                                onChange={handleInputChange}
                                            >
                                                {audienceOptions.map(opt => (
                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                ))}
                                            </select>
                                            <p className="input-hint">Which category of user this plan is assigned to at portal login.</p>
                                        </div>
                                        <div className="form-group-modern">
                                            <label>Price (₹) <span style={{ color: 'var(--danger-color)' }}>*</span></label>
                                            <input
                                                type="number"
                                                name="price"
                                                value={formData.price}
                                                onChange={handleInputChange}
                                                required
                                                min="0"
                                                disabled={formData.type === 'free'}
                                                placeholder={formData.type === 'free' ? 'Free' : 'Enter Price'}
                                            />
                                        </div>
                                        <div className="form-group-modern">
                                            <label htmlFor="plan-validity">Validity <span style={{ color: 'var(--danger-color)' }}>*</span></label>
                                            <div className="plan-input-unit">
                                                <input
                                                    id="plan-validity"
                                                    type="number"
                                                    name="validity"
                                                    value={formData.validity}
                                                    onChange={handleInputChange}
                                                    required
                                                    min="1"
                                                    placeholder="e.g. 2"
                                                />
                                                <select name="validityUnit" value={formData.validityUnit} onChange={handleInputChange} aria-label="Validity unit">
                                                    {VALIDITY_UNITS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                                                </select>
                                            </div>
                                            <p className="input-hint">How long the plan lasts — a voucher's time runs from its first use.</p>
                                        </div>
                                        <div className="form-group-modern">
                                            <label htmlFor="plan-data">Data Limit</label>
                                            <div className="plan-input-unit">
                                                <input
                                                    id="plan-data"
                                                    type="number"
                                                    name="dataLimit"
                                                    value={formData.dataUnlimited || formData.type === 'unlimited' ? '' : formData.dataLimit}
                                                    onChange={handleInputChange}
                                                    placeholder={formData.dataUnlimited || formData.type === 'unlimited' ? 'Unlimited data' : 'e.g. 5'}
                                                    min="0"
                                                    step="any"
                                                    disabled={formData.dataUnlimited || formData.type === 'unlimited'}
                                                />
                                                <select name="dataUnit" value={formData.dataUnit} onChange={handleInputChange} aria-label="Data unit"
                                                    disabled={formData.dataUnlimited || formData.type === 'unlimited'}>
                                                    <option value="MB">MB</option>
                                                    <option value="GB">GB</option>
                                                </select>
                                            </div>
                                            <label className="plan-check">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.dataUnlimited || formData.type === 'unlimited'}
                                                    disabled={formData.type === 'unlimited'}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, dataUnlimited: e.target.checked }))}
                                                />
                                                Unlimited data
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div className="form-section">
                                    <h3 className="section-title">
                                        <i className="fas fa-tachometer-alt"></i>
                                        Bandwidth Control
                                        <span className="plan-section-note">up to {MAX_MBPS} Mbps</span>
                                    </h3>
                                    <div className="form-grid">
                                        <div className="form-group-modern">
                                            <label htmlFor="plan-down">Max Download Speed (Mbps) <span style={{ color: 'var(--danger-color)' }}>*</span></label>
                                            <input
                                                id="plan-down"
                                                type="number"
                                                name="downloadSpeed"
                                                value={formData.downloadSpeed}
                                                onChange={handleInputChange}
                                                required
                                                min="0"
                                                max={MAX_MBPS}
                                                step="any"
                                                placeholder="e.g. 20 (0 = gateway default)"
                                            />
                                        </div>
                                        <div className="form-group-modern">
                                            <label htmlFor="plan-up">Max Upload Speed (Mbps) <span style={{ color: 'var(--danger-color)' }}>*</span></label>
                                            <input
                                                id="plan-up"
                                                type="number"
                                                name="uploadSpeed"
                                                value={formData.uploadSpeed}
                                                onChange={handleInputChange}
                                                required
                                                min="0"
                                                max={MAX_MBPS}
                                                step="any"
                                                placeholder="e.g. 5 (0 = gateway default)"
                                            />
                                        </div>
                                        <div className="form-group-modern">
                                            <label htmlFor="plan-min-down">Min Download Speed (Mbps)</label>
                                            <input
                                                id="plan-min-down"
                                                type="number"
                                                name="minDownloadSpeed"
                                                value={formData.minDownloadSpeed}
                                                onChange={handleInputChange}
                                                min="0"
                                                max={MAX_MBPS}
                                                step="any"
                                                placeholder="Empty = none guaranteed"
                                            />
                                            <p className="input-hint">Guaranteed even when the link is busy.</p>
                                        </div>
                                        <div className="form-group-modern">
                                            <label htmlFor="plan-min-up">Min Upload Speed (Mbps)</label>
                                            <input
                                                id="plan-min-up"
                                                type="number"
                                                name="minUploadSpeed"
                                                value={formData.minUploadSpeed}
                                                onChange={handleInputChange}
                                                min="0"
                                                max={MAX_MBPS}
                                                step="any"
                                                placeholder="Empty = none guaranteed"
                                            />
                                        </div>
                                        <div className="form-group-modern full-width">
                                            <label htmlFor="plan-queue">Speed Control</label>
                                            <select id="plan-queue" name="queueType" value={formData.queueType} onChange={handleInputChange}>
                                                <option value="per_user">Fixed — every guest gets the max speed</option>
                                                <option value="dynamic">Dynamic — share the gateway's internet between min and max, by use</option>
                                                {formData.queueType === 'shared' && <option value="shared">Shared queue (on the router)</option>}
                                            </select>
                                            <p className="input-hint">
                                                Dynamic: while the gateway's internet has room everyone gets the max; when it fills up, each guest gets a fair
                                                share between the min and max (set the gateway's internet speed under Live Bandwidth). Changes reach the router live.
                                            </p>
                                        </div>
                                        {speedError && <p className="plan-form-error full-width" role="alert">{speedError}</p>}
                                        <div className="form-group-modern full-width">
                                            <label>Description</label>
                                            <textarea
                                                name="description"
                                                value={formData.description}
                                                onChange={handleInputChange}
                                                rows="3"
                                                placeholder="Optional description"
                                            ></textarea>
                                        </div>
                                    </div>
                                </div>

                                <div className="form-section">
                                    <h3 className="section-title">
                                        <i className="fas fa-hourglass-half"></i>
                                        Session Limits
                                    </h3>
                                    <div className="form-grid">
                                        <div className="form-group-modern">
                                            <label>Max Session Duration (minutes)</label>
                                            <input
                                                type="number"
                                                name="maxSessionDuration"
                                                value={formData.maxSessionDuration}
                                                onChange={handleInputChange}
                                                min="0"
                                                placeholder="Empty = Unlimited"
                                            />
                                            <p className="input-hint">NAS auto-disconnects the session after this long, even if idle time remains.</p>
                                        </div>
                                        <div className="form-group-modern">
                                            <label>Idle Timeout (minutes)</label>
                                            <input
                                                type="number"
                                                name="idleTimeout"
                                                value={formData.idleTimeout}
                                                onChange={handleInputChange}
                                                min="0"
                                                placeholder="Empty = Unlimited"
                                            />
                                            <p className="input-hint">Auto-logout if there is no data activity for this long.</p>
                                        </div>
                                        <div className="form-group-modern">
                                            <label>Max Concurrent Devices</label>
                                            <input
                                                type="number"
                                                name="maxConcurrentSessions"
                                                value={formData.maxConcurrentSessions}
                                                onChange={handleInputChange}
                                                min="1"
                                                placeholder="1"
                                            />
                                            <p className="input-hint">How many devices can be logged in at once on this plan.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer-modern">
                                <button type="button" className="btn-secondary-modern" onClick={() => setShowModal(false)}>Cancel</button>
                                <button type="submit" className="btn-primary-modern" disabled={!!speedError}>
                                    <i className="fas fa-check"></i>
                                    {editingPlan ? 'Update Plan' : 'Create Plan'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal-overlay-modern" onClick={() => setShowDeleteModal(false)}>
                    <div className="modal-modern modal-small" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern danger">
                            <div>
                                <h2>Delete Plan</h2>
                            </div>
                            <button className="modal-close" onClick={() => setShowDeleteModal(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body-modern">
                            <div style={{ padding: '1rem', background: '#fef2f2', borderRadius: '8px', color: '#991b1b', marginBottom: '1rem' }}>
                                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem' }}>
                                    <i className="fas fa-exclamation-triangle"></i>
                                    <strong>Warning</strong>
                                </div>
                                <p>Are you sure you want to delete plan <strong>{planToDelete?.name}</strong>?</p>
                                <p style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>
                                    This plan will be removed permanently. Active user sessions may be affected.
                                </p>
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <button className="btn-secondary-modern" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                            <button className="btn-danger-modern" onClick={confirmDelete}>
                                <i className="fas fa-trash"></i>
                                Delete Plan
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Plans;
